import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UsersUserResponse } from '@libs/generated-types/users';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { SelfserviceDraftSelfServiceSubmission, SelfserviceSelfServiceSubmission } from '../../../../../../libs/shared/generated/src/lib/types/selfservice';
import { AddressDeclarationComponent } from "../../@core/components/pre-employment/pre-employment-activities/pre-employment-selfservice-dialog/forms/address-declaration/address-declaration.component";
import { CommerceTaxReliefComponent } from '../../@core/components/pre-employment/pre-employment-activities/pre-employment-selfservice-dialog/forms/commerce-tax-relief/commerce-tax-relief.component';
import type { EmploymentHistoryField } from '../../@core/components/pre-employment/pre-employment-activities/pre-employment-selfservice-dialog/forms/employment-history/employment-history.component';
import { EmploymentHistoryComponent } from "../../@core/components/pre-employment/pre-employment-activities/pre-employment-selfservice-dialog/forms/employment-history/employment-history.component";
import { FirstEmploymentComponent } from "../../@core/components/pre-employment/pre-employment-activities/pre-employment-selfservice-dialog/forms/first-employment/first-employment.component";
import { PaymentDetailsComponent } from "../../@core/components/pre-employment/pre-employment-activities/pre-employment-selfservice-dialog/forms/payment-details/payment-details.component";
import { PersonalContactsComponent } from "../../@core/components/pre-employment/pre-employment-activities/pre-employment-selfservice-dialog/forms/personal-contacts/personal-contacts.component";
import { PreviousWorkActivitiesComponent } from "../../@core/components/pre-employment/pre-employment-activities/pre-employment-selfservice-dialog/forms/previous-work-activities/previous-work-activities.component";
import { RetirementPlanComponent } from "../../@core/components/pre-employment/pre-employment-activities/pre-employment-selfservice-dialog/forms/retirement-plan/retirement-plan.component";
import {
  TfrDeclarationComponent,
  tfrDeclarationOptionsValidator,
} from "../../@core/components/pre-employment/pre-employment-activities/pre-employment-selfservice-dialog/forms/tfr-declaration/tfr-declaration.component";
import { TourismTaxReliefComponent } from '../../@core/components/pre-employment/pre-employment-activities/pre-employment-selfservice-dialog/forms/tourism-tax-relief/tourism-tax-relief.component';
import { SELF_SERVICE_MAPPERS, SELF_SERVICE_REVERSE_MAPPERS } from '../../@core/components/pre-employment/pre-employment-selfservice.mappers';
import { AddressDeclarationKind, RecrutimentSelfServiceType } from '../../@core/data/recruitment';
import { BankDetailsChangePaymentMethod } from '../../@core/data/selfservice-forms/bank-details-change';
import { PreEmploymentActivitiesService } from '../../services/pre-employment-activities.service';
import { UsersService } from '../../services/users.service';

@Component({
  selector: 'app-pre-employment-selfservice-mobile',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    TranslateModule,
    RetirementPlanComponent,
    PreviousWorkActivitiesComponent,
    TfrDeclarationComponent,
    PersonalContactsComponent,
    PaymentDetailsComponent,
    FirstEmploymentComponent,
    EmploymentHistoryComponent,
    AddressDeclarationComponent,
    CommerceTaxReliefComponent,
    TourismTaxReliefComponent,
    ToastModule
  ],
  templateUrl: './pre-employment-selfservice-mobile.component.html',
  styleUrls: ['./pre-employment-selfservice-mobile.component.scss'],
  standalone: true,
})
export class PreEmploymentSelfserviceMobileComponent implements OnInit {

  formLoaded: boolean = false;
  selfServiceForm: FormGroup;
  RecrutimentSelfServiceType = RecrutimentSelfServiceType;
  selfServiceType: RecrutimentSelfServiceType;
  me: UsersUserResponse | null = null;
  requestId: string | null = null
  isSaving: boolean = false;
  isCompleted: boolean = false
  isDraft: boolean = false;
  tfrConfirmationVisible: boolean = false;
  tfrInCompanyModificationMode: boolean = false;
  hiredAtYear: number;
  confirmedEmploymentHistoryFields: EmploymentHistoryField[] = [];

  constructor(
    private activeRoute: ActivatedRoute,
    private fb: FormBuilder,
    private usersService: UsersService,
    private readonly messageService: MessageService,
    private preEmploymentActivitiesService: PreEmploymentActivitiesService,
    private readonly translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.selfServiceType = this.activeRoute.snapshot.queryParamMap.get('type') as RecrutimentSelfServiceType;
    this.requestId = this.activeRoute.snapshot.queryParamMap.get('request_id') || null;
    if (!this.requestId) {
      this.initializeEmptyForm();
    } else {
      this.initializeFormWithRequestId(this.requestId);
    }
  }


  private initializeEmptyForm(): void {
    this.tfrInCompanyModificationMode = false;
    this.isCompleted = false;
    this.isDraft = false;
    this.confirmedEmploymentHistoryFields = [];
    switch (this.selfServiceType) {
      case RecrutimentSelfServiceType.ADDRESS_DECLARATION:
        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          kind: [AddressDeclarationKind.ITALIAN, Validators.required],
          stateOrRegion: [null, Validators.required],
          provinceOrCounty: [null, Validators.required],
          city: [null, Validators.required],
          createdAt: [new Date()]
        });
        break;
      case RecrutimentSelfServiceType.EMPLOYMENT_HISTORY:
        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          kind: [null, Validators.required],
          sickDaysCount: [null],
          taxableSocialSecurityAmount: [null],
          fringeBenefitsAmount: [null],
          hasSeverancePay: [null],
          createdAt: [new Date()]
        });
        this.usersService.getUsersMe(true).subscribe((user) => (this.me = user));
        this.hiredAtYear = this.me?.hiredAt ? new Date(this.me.hiredAt).getFullYear() : new Date().getFullYear();
        break;
      case RecrutimentSelfServiceType.FIRST_EMPLOYMENT:
        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          isBeforeJan2007: [null, Validators.required],
          createdAt: [new Date()]
        });
        break;
      case RecrutimentSelfServiceType.PAYMENT_DETAILS_CHANGE:

        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          kind: [BankDetailsChangePaymentMethod.BANK_TRANSFER, Validators.required],
          notesAcknowledged: [false, Validators.requiredTrue],
          accountHolder: this.fb.group({
            firstName: [null, Validators.required],
            lastName: [null, Validators.required],
          }),
          accountCoHolder: this.fb.group({
            firstName: [null],
            lastName: [null],
          }),
          iban: [null, Validators.required],
          createdAt: [new Date()]
        });
        break;
      case RecrutimentSelfServiceType.PERSONAL_CONTACTS_DETAILS:
        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          notesAcknowledged: [false, Validators.requiredTrue],
          landlinePhone: [null],
          email: [null, Validators.required],
          mobilePhone: [null, Validators.required],
          createdAt: [new Date()]
        });
        this.usersService.getUsersMe(true).subscribe((user) => (this.me = user));
        this.hiredAtYear = this.me?.hiredAt ? new Date(this.me.hiredAt).getFullYear() : new Date().getFullYear();
        break;
      case RecrutimentSelfServiceType.PREVIOUS_WORK_ACTIVITIES:
        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          noSubordinateEmployment: [null, Validators.required],
          noSubordinateEmploymentUnder6Months: [null, Validators.required],
          selfEmployment: [null, Validators.required],
          coordinatedCollaborationContract: [null, Validators.required],
          hasCompletedSecondarySchool: [null, Validators.required],
          createdAt: [new Date()]
        });
        break;
      case RecrutimentSelfServiceType.RETIREMENT_PLAN:
        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          contributionsBefore1996: [null, Validators.required],
          createdAt: [new Date()]
        });
        break;
      case RecrutimentSelfServiceType.TFR_DECLARATION:
        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          firstTime: [false],
          alreadyInCompany: [false],
          ransom: [false],
          notRansom: [false],
          tfrPrimaryChoice: [null],
          inCompany: [false],
          createdAt: [new Date()]
        }, { validators: tfrDeclarationOptionsValidator });
        break;
      case RecrutimentSelfServiceType.DETASSAZIONE_COMMERCIO:
        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          incomeAcceptance: [false, Validators.requiredTrue],
          income: [null],
          reliefAcceptance: [false],
          acceptance: [false, Validators.requiredTrue],
          identityCardAttachments: [[], Validators.required],
          nightOrHolidayWorkAcceptance: [false],
          createdAt: [new Date()]
        });
        break;
      case RecrutimentSelfServiceType.DETASSAZIONE_TURISMO:
        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          incomeAcceptance: [false, Validators.requiredTrue],
          income: [null],
          reliefAcceptance: [false],
          acceptance: [false, Validators.requiredTrue],
          identityCardAttachments: [[], Validators.required],
          createdAt: [new Date()]
        });
        break;
      default:
        throw new Error('Invalid selfServiceType');
    }
    this.formLoaded = true;
  }

  private initializeFormWithRequestId(requestId: string): void {
    this.initializeEmptyForm()

    this.preEmploymentActivitiesService.getActivityDetail(requestId).subscribe({
      next: (response) => {
        const submission = response.submission;

        if (!submission || submission.activityKind !== 'SELF_SERVICE') {
          return;
        }

        this.isDraft = submission.status === 'draft';
        this.isCompleted = !this.isDraft;
        const reverseMapper = SELF_SERVICE_REVERSE_MAPPERS[this.selfServiceType]
        if (reverseMapper) this.selfServiceForm.patchValue(reverseMapper(submission.selfService as any))

        if (
          this.selfServiceType === RecrutimentSelfServiceType.EMPLOYMENT_HISTORY
          && submission.selfService.selfServiceKind === 'EMPLOYMENT_HISTORY'
        ) {
          this.confirmedEmploymentHistoryFields = Object.keys(
            submission.selfService.employmentHistoryDetails,
          ) as EmploymentHistoryField[];
        }
        if (this.selfServiceType === RecrutimentSelfServiceType.TFR_DECLARATION) {
          this.selfServiceForm.patchValue({ createdAt: new Date(submission.createdAt) });
          this.enableTfrInCompanyModificationIfEligible();
        }
        if (this.isCompleted) {
          this.selfServiceForm.disable();
          this.enableTfrInCompanyModificationIfEligible();
        }
      },
      error: (error) => this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('pre-employment-selfservice-dialog.toast-summary-error-message'),
        detail: this.translate.instant('pre-employment-selfservice-dialog.toast-error-message'),

      }),
    })
  }

  save(): void {
    if (this.selfServiceType === RecrutimentSelfServiceType.TFR_DECLARATION) {
      if (this.selfServiceForm.invalid || (this.tfrInCompanyModificationMode && !this.isTfrInCompanySelected())) {
        this.selfServiceForm.markAllAsTouched();
        return;
      }

      this.tfrConfirmationVisible = true;
      return;
    }

    this.submit();
  }

  onEmploymentHistoryCompleted(): void {
    this.onClose();
  }

  confirmTfrSubmission(): void {
    this.tfrConfirmationVisible = false;

    if (this.tfrInCompanyModificationMode) {
      this.updateTfrDeclarationSubmission();
      return;
    }

    this.submit();
  }

  cancelTfrConfirmation(): void {
    this.tfrConfirmationVisible = false;
  }

  saveDraft(): void {
    if (!this.requestId || this.isCompleted) return;

    const mapper = SELF_SERVICE_MAPPERS[this.selfServiceType]
    if (!mapper) return

    const payload: SelfserviceDraftSelfServiceSubmission = {
      activityKind: 'SELF_SERVICE',
      status: 'draft',
      selfService: mapper(this.selfServiceForm.getRawValue()),
    };

    this.isSaving = true;

    this.preEmploymentActivitiesService.saveDraft(this.requestId, payload).subscribe({
      next: () => {
        this.isSaving = false;
        this.isDraft = true;
        this.onClose();
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('pre-employment-selfservice-dialog.toast-summary-draft-saved'),
          detail: this.translate.instant('pre-employment-selfservice-dialog.toast-draft-saved'),
        });
      },
      error: () => {
        this.isSaving = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('pre-employment-selfservice-dialog.toast-summary-error-message'),
          detail: this.translate.instant('pre-employment-selfservice-dialog.toast-error-message'),
        });
      }
    });
  }

  getTfrConfirmationMessageKey(): string {
    const formValue = this.selfServiceForm.getRawValue();

    if (formValue.firstTime === true && formValue.inCompany === true) {
      return 'selfservice-request.tfr-declaration.confirmation.first-time-company.label';
    }

    if (formValue.firstTime === true) {
      return 'selfservice-request.tfr-declaration.confirmation.first-time-fund.label';
    }

    if (formValue.alreadyInCompany === true) {
      return 'selfservice-request.tfr-declaration.confirmation.already-in-company.label';
    }

    if (formValue.ransom === true) {
      return 'selfservice-request.tfr-declaration.confirmation.ransom.label';
    }

    return 'selfservice-request.tfr-declaration.confirmation.not-ransom.label';
  }

  isTfrInCompanySelected(): boolean {
    return this.selfServiceForm.get('inCompany')?.value === true;
  }

  private enableTfrInCompanyModificationIfEligible(): void {
    if (this.selfServiceType !== RecrutimentSelfServiceType.TFR_DECLARATION || !this.isCompleted) {
      return;
    }

    const formValue = this.selfServiceForm.getRawValue();
    if (formValue.firstTime !== true || formValue.inCompany === true) {
      return;
    }

    this.tfrInCompanyModificationMode = true;
    this.selfServiceForm.get('inCompany')?.enable();
  }

  private updateTfrDeclarationSubmission(): void {
    if (!this.requestId) return;

    this.isSaving = true;
    this.preEmploymentActivitiesService.updateTfrDeclarationSubmission(this.requestId, { inCompany: true }).subscribe({
      next: () => {
        this.isSaving = false;
        this.isDraft = false;
        this.isCompleted = true;
        this.onClose();
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('pre-employment-selfservice-dialog.toast-summary-success-message'),
          detail: this.translate.instant('pre-employment-selfservice-dialog.toast-success-message'),
        });
      },
      error: () => {
        this.isSaving = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('pre-employment-selfservice-dialog.toast-summary-error-message'),
          detail: this.translate.instant('pre-employment-selfservice-dialog.toast-error-message'),
        });
      },
    });
  }

  private submit(): void {

    if (!this.requestId) return;

    const mapper = SELF_SERVICE_MAPPERS[this.selfServiceType]
    if (!mapper) return

    const payload: SelfserviceSelfServiceSubmission = {
      activityKind: 'SELF_SERVICE',
      selfService: mapper(this.selfServiceForm.getRawValue())
    }
    this.isSaving = true;

    this.preEmploymentActivitiesService.submitActivity(this.requestId, payload).subscribe({
      next: () => {
        this.isSaving = false;
        this.isDraft = false;
        if (this.selfServiceType === RecrutimentSelfServiceType.TFR_DECLARATION) {
          this.isCompleted = true;
          this.onClose();
        }
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('pre-employment-selfservice-dialog.toast-summary-success-message'),
          detail: this.translate.instant('pre-employment-selfservice-dialog.toast-success-message'),
        })
      },
      error: (error) => {
        this.isSaving = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('pre-employment-selfservice-dialog.toast-summary-error-message'),
          detail: this.translate.instant('pre-employment-selfservice-dialog.toast-error-message'),

        })
      }
    })
  }

  onClose(): void {
    this.selfServiceForm.reset();
    this.formLoaded = false;
    this.isCompleted = false;
    this.isDraft = false;
    this.tfrConfirmationVisible = false;
    this.tfrInCompanyModificationMode = false;
  }
}